import { TableCell } from '@mui/material';
import { Typography, Button, SimpleTable, Chip, TableCellComment } from '../../design-system';
import { DashboardHeader } from '../dashboard';
import {
  CURRENCIES,
  InvoiceModeToChipColors,
  InvoiceModeToChipLabels,
  InvoiceStatusToChipColors,
  InvoiceStatusToChipLabels,
  InvoiceTypeToChipColors,
  InvoiceTypeToChipLabels,
} from '../../const';
import { CurrencyCode } from '../../types';
import { formatContainerType, momentTextFormat } from '../../utils';
import { i18n } from '../../i18n';
import { useStore } from '../../context';
import styled from 'styled-components';

export const InvoicesViewHeader = ({ invoice }: any) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.invoicesView.title')} {invoice.invoiceNumber}: {invoice.clientYard.yard.name}{' '}
        {'<->'} {invoice.clientYard.client.code}
      </Typography>
      <Button size={'medium'} data-color={'white'} to={-1}>
        {i18n.t('button.goBack')}
      </Button>
      <Button size={'medium'} onClick={window.print}>
        {i18n.t('button.print')}
      </Button>
    </DashboardHeader>
  );
};

const InvoicesViewSubHeaderBase = styled.div`
  display: grid;
  grid-template-columns: repeat(4, max-content);
  gap: 1.2rem;
`;

export const InvoicesViewSubHeader = ({ invoice }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const mode = invoice.mode as keyof typeof InvoiceModeToChipLabels;
  const type = invoice.type as keyof typeof InvoiceTypeToChipLabels;
  const status = invoice.status as keyof typeof InvoiceStatusToChipLabels;

  return (
    <InvoicesViewSubHeaderBase>
      <Chip
        size={'small'}
        label={InvoiceModeToChipLabels[mode]}
        color={InvoiceModeToChipColors[mode]}
      />
      <Chip
        size={'small'}
        label={InvoiceTypeToChipLabels[type]}
        color={InvoiceTypeToChipColors[type]}
      />
      <Chip
        size={'small'}
        label={InvoiceStatusToChipLabels[status]}
        color={InvoiceStatusToChipColors[status]}
      />
      <Typography variant={'textM'}>
        {momentTextFormat(invoice.periodStart, user)} - {momentTextFormat(invoice.periodEnd, user)}
      </Typography>
    </InvoicesViewSubHeaderBase>
  );
};

export const InvoicesViewLinesTable = ({ invoice }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const rows = Array.isArray(invoice?.lines) ? invoice.lines : [];
  const symbol = CURRENCIES[invoice.currencyCode as CurrencyCode]?.symbol || '';

  return (
    <SimpleTable
      data={{ data: rows, totalCount: rows.length }}
      tableRowProps={(row: any, i: number) => ({
        key: row.id || i,
        onClick: () => {},
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('label.containerNumber'),
          body: (line: any) => (
            <TableCell component={'th'} align={'left'}>
              {line.container?.number || line.containerNumber || ''}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.type'),
          body: (line: any) => (
            <TableCell align={'left'}>{formatContainerType(line.container?.type)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.periodStart'),
          body: (line: any) => (
            <TableCell align={'left'}>{momentTextFormat(line.periodStart, user)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.periodEnd'),
          body: (line: any) => (
            <TableCell align={'left'}>{momentTextFormat(line.periodEnd, user)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.comment'),
          body: (line) => <TableCellComment comment={line.comment || ''} />,
          column: 15,
          isHidden: () => invoice.type !== 'OTHER',
        },
        {
          headText: () => i18n.t('label.storageAmount'),
          body: (line: any) => (
            <TableCell align={'left'}>
              {line.storageAmount}
              {symbol}
            </TableCell>
          ),
          isHidden: () => invoice.type !== 'STORAGE' && invoice.type !== 'ALL',
        },
        {
          headText: () => i18n.t('label.craneInAmount'),
          body: (line: any) => (
            <TableCell align={'left'}>
              {line.gateInCraneAmount}
              {symbol}
            </TableCell>
          ),
          isHidden: () => invoice.type !== 'CRANE' && invoice.type !== 'ALL',
        },
        {
          headText: () => i18n.t('label.craneOutAmount'),
          body: (line: any) => (
            <TableCell align={'left'}>
              {line.gateOutCraneAmount}
              {symbol}
            </TableCell>
          ),
          isHidden: () => invoice.type !== 'CRANE' && invoice.type !== 'ALL',
        },
        {
          headText: () => i18n.t('label.otherAmount'),
          body: (line: any) => (
            <TableCell align={'left'}>
              {line.otherAmount}
              {symbol}
            </TableCell>
          ),
          isHidden: () => invoice.type !== 'OTHER',
        },
        {
          headText: () => i18n.t('label.price'),
          body: (line: any) => (
            <TableCell align={'left'}>
              {line.amount}
              {symbol}
            </TableCell>
          ),
          isHidden: () => invoice.type === 'STORAGE' || invoice.type === 'OTHER',
        },
      ]}
    />
  );
};
